/* Fit funnel: shared fit state (same schema and storage key as the Next.js FitProvider), the quiz and the results page.
   Question copy and options come from the section's JSON (sections/fit-quiz.liquid); shared lookups from snippets/fit-config.liquid. */
(function () {
  'use strict';

  var configElement = document.querySelector('[data-fit-config]');
  if (!configElement) return;
  var config = JSON.parse(configElement.textContent);
  var KEY = config.storageKey;
  var shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
  var pairHandles = config.activities.map(function (a) { return a.handle; }).filter(Boolean);

  window.F2F = window.F2F || {};

  /* ---------- helpers ---------- */
  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function isHeritageKey(key) { return typeof key === 'string' && config.heritageKeys.indexOf(key) !== -1; }
  function sizeLabel(profile) { return [profile.shoeCategory, profile.shoeSize].filter(Boolean).join(' '); }

  /* ---------- state (mirror of lib/fit2you.ts) ---------- */
  function emptyProfile() {
    return { goal: '', arch: '', frequency: '', conditions: [], rightPain: [], leftPain: [], shoeCategory: '', shoeSize: '', shoeType: '', phone: '' };
  }
  function emptyState() {
    return { schemaVersion: 3, heritageItems: [], promoRate: 0, promoCode: '', promoMode: '', profile: emptyProfile(), step: 0, completed: false, inCart: false, quantity: 1, evaluation: false, shoeTypes: [], activitySelection: false, activityPairId: '' };
  }
  function sanitizeState(value) {
    var base = emptyState();
    if (!value || typeof value !== 'object') return base;
    var p = value.profile;
    if (p && typeof p === 'object') {
      Object.keys(base.profile).forEach(function (key) {
        if (key === 'conditions' || key === 'rightPain' || key === 'leftPain') {
          base.profile[key] = Array.isArray(p[key]) ? p[key].filter(function (v) { return typeof v === 'string'; }).slice(0, 15) : [];
        } else if (typeof p[key] === 'string') {
          base.profile[key] = p[key].slice(0, 2000);
        }
      });
    }
    base.promoRate = value.promoRate === 15 || value.promoRate === 20 ? value.promoRate : 0;
    base.promoCode = typeof value.promoCode === 'string' && /^[A-Za-z0-9_-]{1,50}$/.test(value.promoCode) ? value.promoCode : '';
    base.promoMode = value.promoMode === 'live' || value.promoMode === 'preview' ? value.promoMode : '';
    if (!base.promoCode || !base.promoMode) base.promoRate = 0;
    /* Six questions. Restart the displayed step for older drafts; preserve their answers and cart. */
    base.step = value.schemaVersion === 3 && Number.isInteger(value.step) ? Math.min(5, Math.max(0, value.step)) : 0;
    base.heritageItems = Array.isArray(value.heritageItems) ? value.heritageItems.filter(isHeritageKey).slice(0, 2) : [];
    base.quantity = [1, 2, 3].indexOf(value.quantity) !== -1 ? value.quantity : 1;
    base.completed = value.completed === true;
    base.inCart = value.inCart === true;
    base.activitySelection = value.activitySelection === true;
    base.activityPairId = typeof value.activityPairId === 'string' && /^[a-zA-Z0-9-]{1,80}$/.test(value.activityPairId) ? value.activityPairId : '';
    base.evaluation = value.evaluation === true;
    base.shoeTypes = Array.isArray(value.shoeTypes) ? value.shoeTypes.filter(function (v) { return config.shoeTypes.indexOf(v) !== -1; }).slice(0, 3) : [];
    return base;
  }

  /* Reference-compatible links (legacy quiz and cart URLs) import once. Only fit answers are read; names and emails are ignored. */
  function importProfile(params) {
    var mapping = { 'Goal': 'goal', 'Arch Type': 'arch', 'Pain Duration': 'frequency', 'Conditions': 'conditions', 'Right Foot Pain Area': 'rightPain', 'Left Foot Pain Area': 'leftPain', 'Shoe Size': 'shoeSize', 'Shoe Type': 'shoeType' };
    var props = {};
    try {
      var encoded = params.get('properties');
      if (encoded && encoded.length < 16000) props = JSON.parse(atob(encoded));
    } catch (e) { /* Invalid legacy links fall back to ordinary empty-cart behaviour. */ }
    var result = {};
    Object.keys(mapping).forEach(function (external) {
      var field = mapping[external];
      var v = params.get(external);
      if (v == null && props) v = props[external];
      if (typeof v !== 'string' || !v.trim() || /^_+$/.test(v)) return;
      if (field === 'conditions' || field === 'rightPain' || field === 'leftPain') result[field] = v.split(',').map(function (s) { return s.trim(); }).slice(0, 15);
      else result[field] = v.slice(0, 2000);
    });
    var sizing = result.shoeSize && result.shoeSize.match(/^(Mens|Womens|Kids)\s+(.+)$/i);
    if (sizing) {
      result.shoeCategory = sizing[1][0].toUpperCase() + sizing[1].slice(1).toLowerCase();
      result.shoeSize = sizing[2];
    }
    return Object.keys(result).length ? result : null;
  }

  function persist(state) {
    try { localStorage.setItem(KEY, JSON.stringify(state)); return true; } catch (e) { return false; }
  }

  function createStore() {
    var state = emptyState();
    var storageAvailable = true;
    try {
      var raw = localStorage.getItem(KEY) || sessionStorage.getItem(KEY);
      try { state = sanitizeState(JSON.parse(raw || 'null')); } catch (e) { /* Corrupt drafts start afresh. */ }
    } catch (e) { storageAvailable = false; }
    var params = new URLSearchParams(window.location.search);
    var imported = importProfile(params);
    if (imported) {
      state = emptyState();
      state.profile = Object.assign(state.profile, imported);
      state.completed = params.get('quiz_complete') === 'true' || Boolean(params.get('properties'));
      state.inCart = /\/cart$/.test(window.location.pathname);
      state.shoeTypes = state.profile.shoeType ? [state.profile.shoeType] : [];
      window.history.replaceState(null, '', window.location.pathname);
    }
    var launchShoes = { golf: 'Golf Shoes', tennis: 'Pickleball Shoes', lifestyle: 'Sneakers', dress: 'Dress Shoes' };
    var selectedActivity = params.get('activity');
    if (/\/pages\/quiz$/.test(window.location.pathname) && Object.prototype.hasOwnProperty.call(launchShoes, selectedActivity)) {
      state.profile.shoeType = launchShoes[selectedActivity];
      state.shoeTypes = [launchShoes[selectedActivity]];
      state.completed = false;
      state.activitySelection = true;
      state.activityPairId = pairId();
      state.step = 0;
      params.delete('activity');
      window.history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash);
    }
    storageAvailable = persist(state) && storageAvailable;
    return {
      get state() { return state; },
      get storageAvailable() { return storageAvailable; },
      update: function (change) {
        state = Object.assign({}, state, change);
        storageAvailable = persist(state);
        return state;
      }
    };
  }

  /* ---------- activity match (fitActivity) ---------- */
  function fitActivity(shoe) {
    var found = null;
    var fallback = null;
    config.activities.forEach(function (a) {
      if (a.fallback) fallback = a;
      else if (a.shoes.indexOf(shoe) !== -1) found = a;
    });
    return found || fallback;
  }

  /* ---------- Shopify cart helpers ---------- */
  function request(url, options) {
    var settings = Object.assign({ headers: { Accept: 'application/json' }, credentials: 'same-origin' }, options);
    return fetch(url, settings).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (body) {
        if (!response.ok) throw new Error(body.description || body.message || 'The store could not complete that request.');
        return body;
      });
    });
  }
  function post(path, payload) {
    return request(shopifyRoot + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
  }
  function availableVariant(handle) {
    return request(shopifyRoot + 'products/' + handle + '.js').then(function (product) {
      var variant = (product.variants || []).filter(function (v) { return v.available; })[0];
      if (!variant) throw new Error('This pair is not available to order online yet. Please contact Fore2Feet.');
      return variant;
    }, function () { throw new Error('This pair could not be found in the store. Please contact Fore2Feet.'); });
  }
  function pairProperties(profile, footwear, number) {
    var props = { '_fit_pair_id': pairId(), 'Product': 'MotionForm3D', 'Pair': String(number), 'Footwear': footwear, 'Scan status': 'Required after purchase', '_scan_required': 'true' };
    var size = sizeLabel(profile);
    if (size) props['Shoe size'] = size;
    if (profile.shoeCategory) props['Sizing system'] = profile.shoeCategory;
    return props;
  }
  function pairId() {
    return window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }
  /* Keep unrelated properties. Shopify replaces the complete properties object on change.
     A multi-quantity line is split so one person's details never overwrite a sibling pair. */
  function updatePair(key, patch, variantId) {
    return request(shopifyRoot + 'cart.js').then(function (cart) {
      var item = cart.items.find(function (line) { return line.key === key; });
      if (!item) throw new Error('Your bag changed. Review this pair and try again.');
      var properties = Object.assign({}, item.properties || {}, patch);
      Object.keys(properties).forEach(function (name) { if (properties[name] === null) delete properties[name]; });
      properties._fit_pair_id = item.quantity === 1 && properties._fit_pair_id ? properties._fit_pair_id : pairId();
      if (pairHandles.indexOf(item.handle) !== -1 && (item.quantity > 1 || !properties.Pair)) {
        var used = cart.items.map(function (line) { return Number((line.properties || {}).Pair) || 0; });
        var number = 1;
        while (used.indexOf(number) !== -1) number += 1;
        properties.Pair = String(number);
      }
      var id = variantId || item.variant_id || item.id;
      if (Number(id) === Number(item.variant_id || item.id) && item.quantity === 1) {
        return post('cart/change.js', { id: item.key, quantity: 1, properties: properties });
      }
      var addition = { id: id, quantity: 1, properties: properties };
      if (item.selling_plan_allocation) addition.selling_plan = item.selling_plan_allocation.selling_plan.id;
      return post('cart/add.js', { items: [addition] }).then(function () {
        /* Adding can change discount-derived keys; locate the old line again. */
        return request(shopifyRoot + 'cart.js');
      }).then(function (fresh) {
        var original = fresh.items.find(function (line) {
          return Number(line.variant_id || line.id) === Number(item.variant_id || item.id) &&
            JSON.stringify(line.properties || {}) === JSON.stringify(item.properties || {});
        });
        if (!original) throw new Error('Your bag changed. Review the pairs before continuing.');
        return post('cart/change.js', { id: original.key, quantity: original.quantity - 1 });
      }).catch(function (error) {
        /* Roll back only the new uniquely identified line; never remove the original. */
        return request(shopifyRoot + 'cart.js').then(function (fresh) {
          var added = fresh.items.find(function (line) { return (line.properties || {})._fit_pair_id === properties._fit_pair_id && Number(line.variant_id || line.id) === Number(id); });
          var original = fresh.items.find(function (line) { return Number(line.variant_id || line.id) === Number(item.variant_id || item.id) && JSON.stringify(line.properties || {}) === JSON.stringify(item.properties || {}); });
          if (added && original && original.quantity === item.quantity) return post('cart/change.js', { id: added.key, quantity: 0 });
          if (added && (!original || original.quantity === item.quantity - 1)) return;
        }).then(function () { throw error; }, function () { throw new Error('Your bag could not finish updating. Review its pairs before continuing.'); });
      });
    });
  }
  /* Order level fit answers, using the attribute names of the legacy quiz links so importProfile reads them back. */
  function profileAttributes(profile) {
    var attributes = {
      'Goal': profile.goal, 'Arch Type': profile.arch, 'Pain Duration': profile.frequency, 'Conditions': profile.conditions.join(', '),
      'Right Foot Pain Area': profile.rightPain.join(', '), 'Left Foot Pain Area': profile.leftPain.join(', '),
      'Shoe Size': sizeLabel(profile), 'Shoe Type': profile.shoeType, 'Source': 'MotionForm3D funnel'
    };
    Object.keys(attributes).forEach(function (k) { if (!attributes[k]) delete attributes[k]; });
    return attributes;
  }
  /* Answer edits preserve the bag. Explicit activity cards ensure their matched pair, retaining existing items and the pair limit. */
  function ensurePair(profile, activitySelection, activityPairId) {
    return request(shopifyRoot + 'cart.js').then(function (cart) {
      var pairs = cart.items.filter(function (item) { return pairHandles.indexOf(item.handle) !== -1; });
      var hasPair = pairs.length > 0;
      var attributes = function () { return post('cart/update.js', { attributes: profileAttributes(profile) }); };
      if (!hasPair && !profile.shoeType) return attributes().then(function () { return { needsActivity: true }; });
      var activity = fitActivity(profile.shoeType);
      if (activitySelection && activityPairId && pairs.some(function (item) { return (item.properties || {})._fit_pair_id === activityPairId; })) return attributes();
      if (hasPair && !activitySelection) {
        var only = pairs.length === 1 && pairs[0].quantity === 1 ? pairs[0] : null;
        if (only && !(only.properties || {})['Shoe size'] && !(only.properties || {})._fit_details_saved && sizeLabel(profile)) {
          return updatePair(only.key, { 'Shoe size': sizeLabel(profile), 'Sizing system': profile.shoeCategory || '' }).then(attributes);
        }
        return attributes();
      }
      var pairCount = pairs.reduce(function (sum, item) { return sum + item.quantity; }, 0);
      if (pairCount >= config.maxPairs) throw new Error('Your bag already has three MotionForm3D pairs. Open Cart to change a pair’s activity before continuing.');
      var pairNumber = pairs.reduce(function (max, item) { return Math.max(max, Number((item.properties || {}).Pair) || 0); }, 0) + 1;
      if (!activity.handle) throw new Error('This activity is not available to order online yet. Please contact Fore2Feet.');
      return availableVariant(activity.handle).then(function (variant) {
        var properties = pairProperties(profile, profile.shoeType || 'Sneakers', pairNumber);
        if (activitySelection && activityPairId) properties._fit_pair_id = activityPairId;
        return post('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties: properties }] });
      }).then(attributes);
    });
  }

  window.F2F.fit = {
    config: config, esc: esc, emptyState: emptyState, sanitizeState: sanitizeState, createStore: createStore, fitActivity: fitActivity,
    request: request, post: post, availableVariant: availableVariant, pairProperties: pairProperties, updatePair: updatePair, profileAttributes: profileAttributes,
    ensurePair: ensurePair, pairHandles: pairHandles, root: shopifyRoot
  };

  /* Pill headers gain their rounded-bottom state after a little scrolling (FitHeader). */
  var headers = document.querySelectorAll('.fit-pill-header, .cart-pill-header');
  if (headers.length) {
    var onScroll = function () {
      headers.forEach(function (h) { h.classList.toggle('is-scrolled', window.scrollY > 24); });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- quiz ---------- */
  function initQuiz(root) {
    var data = JSON.parse(root.querySelector('[data-quiz-data]').textContent);
    var ui = data.ui;
    var questions = data.questions;
    var store = createStore();
    var params = new URLSearchParams(window.location.search);
    if (store.state.completed && !params.has('edit')) {
      window.location.replace(config.urls.cart);
      return;
    }
    if (store.state.activitySelection && !store.state.activityPairId) store.update({ activityPairId: pairId() });
    var body = root.querySelector('[data-quiz-body]');
    var form = root.querySelector('[data-quiz-form]');
    var progress = root.querySelector('[data-quiz-progress]');
    var back = root.querySelector('[data-quiz-back]');
    var next = root.querySelector('[data-quiz-next]');
    var portrait = null;
    var side = null;
    var busy = false;
    var error = '';

    function step() { return Math.min(store.state.step, questions.length - 1); }
    function current() { return questions[step()]; }

    function sizesFor(category) {
      if (category === 'Kids') return data.sizes.kids;
      var list = [];
      for (var i = 0; i < data.sizes.adultCount; i += 1) list.push(String(data.sizes.adultFrom + i * data.sizes.adultStep));
      return category ? list : list.concat(data.sizes.kids);
    }

    function optionHtml(q, option, index, selected) {
      var visual = '';
      if (q.visual) {
        var image = q.images && q.images[index];
        visual = '<span class="fit-option-visual">' + (image
          ? '<img src="' + esc(image) + '" alt="' + esc(option) + '" width="' + q.imageSize + '" height="' + q.imageSize + '" loading="eager" decoding="async">'
          : '<span class="fit-unknown">?</span>') + '</span>';
      }
      return '<label class="fit-option ' + (selected ? 'is-selected' : '') + '"><input type="' + (q.multiple ? 'checkbox' : 'radio') + '" name="' + esc(q.field) + '" value="' + esc(option) + '"' + (selected ? ' checked' : '') + '>' +
        '<span class="fit-option-content">' + visual + '<span class="fit-option-label"><kbd>' + String.fromCharCode(65 + index) + '</kbd><span>' + esc(option) + '</span><span class="fit-option-check" aria-hidden="true">' + (selected ? '✓' : '') + '</span></span></span></label>';
    }

    function render(focusHeading) {
      var index = step();
      var q = current();
      var profile = store.state.profile;
      var value = profile[q.field];
      var title = esc(q.title);
      if (q.field === 'rightPain') title = title.replace(/\bright\b/i, '<strong class="fit-question-side">$&</strong>');
      if (q.field === 'leftPain') title = title.replace(/\bleft\b/i, '<strong class="fit-question-side">$&</strong>');
      var selected = profile.shoeType ? fitActivity(profile.shoeType) : null;
      var price = selected && config.prices ? config.prices[selected.handle] : '';
      var customizing = store.state.activitySelection && selected;
      var finalLabel = customizing ? 'Add My Custom Pair to Cart' + (price ? ' — ' + price : '') : 'Save Answers and Continue';
      var summary = '<div class="customize-question-intro"><p class="fit-eyebrow">Personalize Your Orthotics · Step 2 of 3</p><h2>Let’s Make These Yours.</h2><p>Answer a few questions about your feet so we can prepare your custom order.</p>';
      if (customizing) summary += '<p class="customize-selected-pair"><strong>' + esc(selected.name) + ' Custom Orthotics ' + esc(price) + '</strong><br>Activity selected: ' + esc(selected.name) + '</p><p>Your pair will be added to your cart after you finish customizing.</p>';
      else summary += '<p>Update your answers, then review your cart or choose your pair.</p>';
      summary += '</div>';
      var html = summary + '<div class="fit-question-meta"><span class="fit-eyebrow">' + esc(ui.eyebrow) + '</span><span>' + 'Question ' + (index + 1) + ' of ' + questions.length + '</span></div>' +
        '<h1 tabindex="-1" id="fit-question-title"><span class="fit-question-number">' + (index + 1) + '</span>' + title + '</h1>' +
        '<p id="fit-question-description" class="fit-question-description">' + esc(q.description) + '</p>';
      if (q.options) {
        html += '<fieldset aria-labelledby="fit-question-title" aria-describedby="fit-question-description"><legend class="fit-sr-only">' + esc(q.title) + '</legend>';
        if (q.multiple) html += '<p class="fit-choice-hint">' + esc(ui.multipleHint) + (q.optional ? ' · ' + esc(ui.optional) : '') + '</p>';
        html += '<div class="' + (q.visual ? 'fit-visual-options fit-visual-' + q.visual : 'fit-options' + (q.long ? ' fit-options-long' : '') + (q.columns === 2 ? ' fit-options-two' : '')) + '">';
        q.options.forEach(function (option, i) {
          var selected = Array.isArray(value) ? value.indexOf(option) !== -1 : value === option;
          html += optionHtml(q, option, i, selected);
        });
        html += '</div></fieldset>';
      }
      if (q.control === 'size') {
        html += '<label class="fit-field"><span>' + esc(ui.sizeLabel) + '</span><select aria-label="' + esc(ui.sizeLabel) + '"><option value="">' + esc(ui.sizePlaceholder) + '</option>';
        sizesFor(profile.shoeCategory).forEach(function (size) {
          [size, size + ' Wide'].forEach(function (label) {
            html += '<option value="' + esc(label) + '"' + (profile.shoeSize === label ? ' selected' : '') + '>' + esc(label) + '</option>';
          });
        });
        html += '</select></label>';
      }
      html += '<p class="fit-error" role="alert" data-quiz-error' + (error ? '' : ' hidden') + '>' + esc(error) + '</p>';
      html += '<div class="fit-question-actions"><button class="fit-button" type="submit" data-quiz-submit>' + esc(index === questions.length - 1 ? finalLabel : ui.ok) + '<span aria-hidden="true">→</span></button><span class="fit-keyboard-hint">' + ui.keyboardHint + '</span></div>';
      if (!store.storageAvailable) html += '<p class="fit-notice" role="status">' + esc(ui.storageNotice) + '</p>';
      form.innerHTML = html;

      body.classList.toggle('fit-quiz-with-portrait', Boolean(q.portrait));
      body.classList.toggle('fit-quiz-with-side', Boolean(q.sideImage && q.sideImage.src));
      body.classList.toggle('fit-quiz-long', q.visual === 'pain' || Boolean(q.long));
      renderPortrait(q.portrait);
      renderSide(q.sideImage);
      progress.setAttribute('aria-valuemax', String(questions.length));
      progress.setAttribute('aria-valuetext', 'Question ' + (index + 1) + ' of ' + questions.length);
      progress.setAttribute('aria-valuenow', String(index + 1));
      progress.firstElementChild.style.width = ((index + 1) / questions.length * 100) + '%';
      back.disabled = index === 0;
      next.setAttribute('aria-label', index === questions.length - 1 ? ui.finish : ui.next);
      if (focusHeading) {
        form.querySelector('#fit-question-title').focus({ preventScroll: true });
        window.scrollTo(0, 0);
      }
    }

    function renderPortrait(p) {
      if (!p) {
        if (portrait) { portrait.remove(); portrait = null; }
        return;
      }
      if (!portrait) {
        portrait = document.createElement('figure');
        portrait.className = 'fit-quiz-portrait';
        body.appendChild(portrait);
      }
      portrait.innerHTML = '<img src="' + esc(p.src) + '" alt="' + esc(p.alt) + '" sizes="(max-width: 800px) 100vw, 38vw" loading="eager" decoding="async" style="position: absolute; height: 100%; width: 100%; inset: 0px;">' +
        '<figcaption><span class="fit-eyebrow">' + esc(p.eyebrow) + '</span><strong>' + esc(p.name) + '</strong><span>' + esc(p.role) + '</span></figcaption>';
    }

    /* Optional picture beside a question (editable in the theme editor); removed again when the next question has none. */
    function renderSide(image) {
      if (!image || !image.src) {
        if (side) { side.remove(); side = null; }
        return;
      }
      if (!side) {
        side = document.createElement('figure');
        side.className = 'fit-quiz-side';
        body.appendChild(side);
      }
      side.innerHTML = '<img src="' + esc(image.src) + '" alt="' + esc(image.alt || '') + '" width="627" height="627" loading="eager" decoding="async">';
    }

    function showError(message) {
      error = message;
      var node = form.querySelector('[data-quiz-error]');
      if (!node) return;
      node.textContent = message;
      node.hidden = !message;
    }

    function syncSelection() {
      var q = current();
      var value = store.state.profile[q.field];
      form.querySelectorAll('.fit-option').forEach(function (label) {
        var input = label.querySelector('input');
        var selected = Array.isArray(value) ? value.indexOf(input.value) !== -1 : value === input.value;
        input.checked = selected;
        label.classList.toggle('is-selected', selected);
        label.querySelector('.fit-option-check').textContent = selected ? '✓' : '';
      });
    }

    function answer(value) {
      var q = current();
      var profile = store.state.profile;
      var field = q.field;
      var nextProfile;
      if (q.multiple) {
        var existing = profile[field];
        var values;
        if (value === 'None') values = existing.indexOf('None') !== -1 ? [] : ['None'];
        else if (existing.indexOf(value) !== -1) values = existing.filter(function (v) { return v !== value; });
        else values = existing.filter(function (v) { return v !== 'None'; }).concat(value);
        nextProfile = Object.assign({}, profile);
        nextProfile[field] = values;
      } else {
        nextProfile = Object.assign({}, profile);
        nextProfile[field] = value;
        if (field === 'shoeCategory' && profile.shoeCategory !== value) nextProfile.shoeSize = '';
      }
      store.update({ profile: nextProfile, completed: false });
      showError('');
      syncSelection();
    }

    function finish() {
      var state = store.state;
      var profile = state.profile;
      store.update({ completed: true, inCart: true, profile: profile, shoeTypes: profile.shoeType ? [profile.shoeType].concat(state.shoeTypes.slice(1)) : state.shoeTypes });
      busy = true;
      form.setAttribute('aria-busy', 'true');
      form.querySelector('[data-quiz-submit]').disabled = true;
      ensurePair(profile, state.activitySelection, state.activityPairId).then(function (result) {
        store.update({ activitySelection: false });
        window.location.assign(result && result.needsActivity ? config.urls.shop : config.urls.cart);
      }, function (e) {
        if (state.activitySelection) store.update({ completed: false });
        busy = false;
        form.removeAttribute('aria-busy');
        var button = form.querySelector('[data-quiz-submit]');
        if (button) button.disabled = false;
        showError(e.message);
      });
    }

    function advance() {
      if (busy) return;
      var q = current();
      var value = store.state.profile[q.field];
      if (!q.optional && (!value || (Array.isArray(value) && !value.length))) {
        showError(q.options ? ui.chooseAnswer : ui.completeField);
        return;
      }
      showError('');
      if (step() === questions.length - 1) finish();
      else {
        store.update({ step: step() + 1 });
        render(true);
      }
    }

    function previous() {
      if (busy || step() === 0) return;
      store.update({ step: step() - 1 });
      showError('');
      render(true);
    }

    form.addEventListener('submit', function (event) { event.preventDefault(); advance(); });
    form.addEventListener('change', function (event) {
      var target = event.target;
      if (target.matches('.fit-option input')) answer(target.value);
      else if (target.matches('select')) answer(target.value);
    });
    back.addEventListener('click', previous);
    next.addEventListener('click', advance);

    /* Shortcuts stay on the page so ordinary typing in fields is untouched. */
    root.addEventListener('keydown', function (event) {
      var tag = event.target.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].indexOf(tag) !== -1) return;
      if (event.key === 'Enter') { event.preventDefault(); advance(); }
      if (event.key === 'ArrowUp') { event.preventDefault(); previous(); }
      var q = current();
      var letter = event.key.toUpperCase().charCodeAt(0) - 65;
      if (event.key.length === 1 && q.options && letter >= 0 && letter < q.options.length) {
        event.preventDefault();
        answer(q.options[letter]);
      }
    });

    root.removeAttribute('aria-busy');
    render(true);
  }

  /* ---------- results ---------- */
  function initResults(root) {
    var store = createStore();
    var state = store.state;
    var profile = state.profile;
    var content = root.querySelector('[data-results-content]');
    var minimal = root.querySelector('[data-header-minimal]');
    var full = root.querySelector('[data-header-full]');
    var priceLabel = root.getAttribute('data-price-label');
    var copy = JSON.parse(root.querySelector('[data-results-copy]').textContent);
    var heroImage = root.getAttribute('data-result-image') || '';
    var heroAlt = root.getAttribute('data-result-image-alt') || '';

    function emptyView() {
      minimal.hidden = false;
      content.innerHTML = '<div class="fit-empty"><p class="fit-eyebrow">' + esc(copy.emptyEyebrow) + '</p><h1>' + esc(copy.emptyTitle) + '</h1><p>' + esc(copy.emptyText) + '</p>' +
        '<a href="' + esc(config.urls.quiz) + '" class="fit-button">' + esc(state.step ? copy.emptyContinue : copy.emptyStart) + ' <span aria-hidden="true">→</span></a></div>';
    }

    function resultsView() {
      full.hidden = false;
      var activity = fitActivity(profile.shoeType);
      var hasCondition = profile.conditions.some(function (c) { return c !== 'None'; });
      var archKnown = profile.arch && profile.arch !== 'Not Sure';
      var archCopy = archKnown ? 'You selected ' + profile.arch.toLowerCase() + '. This is your own description; your scan supplies the individual shape used for your printed pair.' : copy.archUnknown;
      var tags = '';
      if (profile.goal) tags += '<span><strong>Goal:</strong> ' + esc(profile.goal) + '</span>';
      if (profile.conditions.length) tags += '<span><strong>Conditions:</strong> ' + esc(profile.conditions.join(', ')) + '</span>';
      if (profile.shoeSize) tags += '<span><strong>Size:</strong> ' + esc(profile.shoeCategory) + ' ' + esc(profile.shoeSize) + '</span>';
      if (profile.shoeType) tags += '<span><strong>Shoes:</strong> ' + esc(profile.shoeType) + '</span>';
      content.innerHTML = '<div class="fit-results-content"><div class="fit-results-heading"><p class="fit-eyebrow">' + 'YOUR MotionForm3D PROFILE' + '</p>' +
        '<h1>Custom Support for ' + esc(profile.shoeType || 'Your Shoes') + '</h1><p>' + esc(copy.subtitle) + '</p><div class="fit-short-rule"></div>' +
        (heroImage ? '<figure class="fit-results-image"><img src="' + esc(heroImage) + '" alt="' + esc(heroAlt) + '" loading="eager" decoding="async"></figure>' : '') + '</div>' +
        '<div class="fit-result-cards">' +
        '<article class="fit-result-card"><span class="fit-result-icon" aria-hidden="true">⚡</span><div><p class="fit-eyebrow">YOUR PERSONAL DETAILS</p><h2>' + esc(hasCondition ? copy.detailsConcernTitle : copy.detailsPlainTitle) + '</h2><p>' +
        esc(hasCondition ? 'You mentioned ' + profile.conditions.join(', ') + '. ' + copy.detailsConcernText : copy.detailsPlainText) + '</p></div></article>' +
        '<article class="fit-result-card"><span class="fit-result-icon"><span aria-hidden="true">🦶</span></span><div><p class="fit-eyebrow">ARCH &amp; FIT</p><h2>' + esc(archKnown ? profile.arch + '. Your scan takes it from here.' : copy.archUnknownTitle) + '</h2><p>' + esc(archCopy) + '</p></div></article>' +
        '<article class="fit-result-card"><span class="fit-result-icon" aria-hidden="true">⛳</span><div><p class="fit-eyebrow">ACTIVITY MATCH</p><h2>MotionForm3D · ' + esc(activity.name) + '</h2><p>' + esc(activity.detail) + '</p></div></article></div>' +
        '<section class="fit-profile-glance" aria-label="Your profile at a glance"><div><p class="fit-eyebrow">YOUR PROFILE AT A GLANCE</p><button type="button" class="fit-quiet-link" data-edit>Edit answers ↗</button></div><div class="fit-profile-tags">' + tags + '</div></section>' +
        '<section class="fit-result-action"><p>' + esc(copy.ready) + '</p><button type="button" class="fit-button" data-add>Get my MotionForm3D <span aria-hidden="true">→</span></button>' +
        '<span class="fit-caption">' + esc(priceLabel) + ' per pair · Scan after purchase</span><p class="fit-error" role="alert" data-results-error hidden></p></section>' +
        '<p class="fit-results-disclaimer">' + esc(copy.disclaimer) + '</p></div>';

      content.querySelector('[data-edit]').addEventListener('click', function () {
        store.update({ step: 0 });
        window.location.assign(config.urls.quiz + '?edit=1');
      });
      var add = content.querySelector('[data-add]');
      add.addEventListener('click', function () {
        add.disabled = true;
        store.update({ inCart: true, shoeTypes: state.shoeTypes.length ? state.shoeTypes : [profile.shoeType] });
        ensurePair(profile).then(function () {
          window.location.assign(config.urls.cart);
        }, function (e) {
          add.disabled = false;
          var node = content.querySelector('[data-results-error]');
          node.textContent = e.message;
          node.hidden = false;
        });
      });
    }

    if (state.completed) resultsView(); else emptyView();
    root.removeAttribute('aria-busy');
  }

  var quiz = document.querySelector('[data-fit-quiz]');
  if (quiz) initQuiz(quiz);
  var results = document.querySelector('[data-fit-results]');
  if (results) initResults(results);
})();
